<template>
  <pf-table v-if="filteredOrderedProducts.length" v-bind="ouiaProps" striped>
    <pf-thead>
      <pf-tr>
        <pf-th v-if="selectable">
          <pf-checkbox :model-value="selectAllCheckboxState" :indeterminate="selectAllIndeterminate" @change="handleSelectAll" />
        </pf-th>
        <pf-th>
          <sort-link v-if="!hideDomainSort" v-model="s" v-model:reverse="r" field="dominio">
            {{ $t('Dominio') }}
          </sort-link>
          <template v-else>{{ $t('Dominio') }}</template>
        </pf-th>
        <pf-th>
          <sort-link v-model="s" v-model:reverse="r" field="nome">
            {{ $t('Nome') }}
          </sort-link>
        </pf-th>
        <pf-th class="price">
          <sort-link v-model="s" v-model:reverse="r" field="prezzo">
            {{ $t('Importo') }}
          </sort-link>
        </pf-th>
        <pf-th>{{ $t('Durata') }}</pf-th>
        <pf-th style="min-width: 130px">
          <sort-link v-model="s" v-model:reverse="r" field="data_fine">
            {{ $t('Scadenza') }}
          </sort-link>
        </pf-th>
        <pf-th style="min-width: 190px">
          {{ $t('Rinnovo') }}
        </pf-th>
        <pf-th style="max-width: 12ch" class="pf-v6-u-text-align-end">
          {{ $t('Azioni') }}
        </pf-th>
      </pf-tr>
    </pf-thead>
    <pf-tbody>
      <pf-tr v-for="(product, idx) in filteredOrderedProducts" :key="product.id">
        <pf-td v-if="selectable">
          <pf-checkbox :model-value="props.selected?.includes(product.id) ?? false" @change="$emit('toggle-select', [product.id])" />
        </pf-td>
        <pf-td :data-label="$t('Dominio')">{{ product.dominio }}</pf-td>
        <pf-td :data-label="$t('Nome')">
          {{ product.nome }}<span v-if="product.qt > 1"> (x{{ product.qt }})</span>{{ ' ' }}
          <pf-label v-if="product.nfr" color="green" :title="$t('Prodotto not for resale')">NFR</pf-label>
          <template v-if="product.note_cliente">
            <br />
            {{ $t('Note') }}: {{ product.note_cliente }}
          </template>
          <template v-if="product.last_bw_upgrade">
            <br />
            <i
              >{{ $t('Upgrade di traffico automatico (in data {0})', [$date(product.last_bw_upgrade)]) }} -
              <template v-if="product.free_bw_upgrade">{{ $t('Primo mese gratuito') }}</template></i
            >
          </template>
        </pf-td>
        <pf-td :data-label="$t('Importo')" class="price">
          {{ $currency(product.prezzo, login.currency) }}
          <template v-if="product.last_bw_upgrade">
            <br />
            <i v-if="product.free_bw_upgrade">{{ $currency(0, login.currency) }}</i>
            <i v-else>{{ $currency(product.bw_upgrade_price, login.currency) }}</i>
          </template>
        </pf-td>
        <pf-td :data-label="$t('Durata')">
          {{ product.durata == 'Annuale' ? $t('Annuale') : product.durata == 'Mensile' ? $t('Mensile') : $t('Una Tantum') }}
        </pf-td>
        <pf-td :data-label="$t('Scadenza')" style="min-width: 160px">
          {{ $date(product.data_fine) }}
          <pf-content component="small">{{ daysFromNow(product.data_fine) }}</pf-content>
        </pf-td>
        <pf-td :data-label="$t('Rinnovo')">
          <template v-if="product.free_period > 0">
            <span v-if="product.days_to_expiration > 0" :class="renewClass(product.disdetto || !product.rinnovo_automatico)">
              {{
                product.disdetto || !product.rinnovo_automatico
                  ? $t('In prova, cancellato al termine del test')
                  : $t('In prova, Verrà rinnovato al termine del test')
              }}
            </span>
            <span v-else :class="renewClass(product.disdetto || !product.rinnovo_automatico)">
              {{
                product.disdetto || !product.rinnovo_automatico
                  ? $t('Periodo di prova terminato. In cancellazione')
                  : $t('Periodo di prova terminato. In fatturazione')
              }}
            </span>
          </template>

          <template v-else-if="product.days_to_expiration < 0">
            <span :class="renewClass(product.disdetto || !product.rinnovo_automatico)">{{
              product.disdetto || !product.rinnovo_automatico ? $t('Scaduto, in cancellazione') : $t('In fatturazione')
            }}</span>
          </template>

          <template v-else-if="product.disdetto">
            <pf-tooltip position="left">
              <template #content>{{ $t('Alla scadenza, verrà sospeso e successivamente cancellato') }}</template>
              <pf-label color="red">{{ $t('Disdetto') }}</pf-label>
            </pf-tooltip>
          </template>

          <template v-else-if="product.pending_invoices !== null">
            <pf-tooltip position="left">
              <template #content>{{
                $t('La fattura nr. {0} che include il rinnovo di questo prodotto non è ancora stata saldata', [product.pending_invoices])
              }}</template>
              <pf-label color="red">{{ $t('Pagamenti arretrati') }}</pf-label>
            </pf-tooltip>
          </template>
          <template v-else>
            <pf-label v-if="product.durata === 'Una tantum'" color="blue">
              {{ $t('Una tantum') }}
            </pf-label>
            <template v-else-if="product.rinnovo_automatico">
              <pf-tooltip position="left">
                <template #content>{{ $t('Alla scadenza verrà rinnovato e fatturato') }}</template>
                <pf-label color="green">{{ $t('Automatico') }}</pf-label>
              </pf-tooltip>
            </template>
            <template v-else>
              <pf-tooltip position="left">
                <template #content>{{ $t('È necessario un tuo intervento per rinnovare il prodotto') }}</template>
                <pf-label color="orange">{{ $t('Manuale') }}</pf-label>
              </pf-tooltip>
            </template>
          </template>
        </pf-td>
        <pf-td class="pf-v6-u-text-align-end">
          <pf-tooltip v-if="product.pending_invoices !== null" position="left">
            <granted-link :to="{ name: 'invoices' }" type="danger" small>
              <template #icon>
                <triangle-exclamation-icon />
              </template>
              {{ $t('Paga ora') }}
            </granted-link>
            <template #content>{{
              $t('La fattura nr. {0} che include il rinnovo di questo prodotto non è ancora stata saldata', [product.pending_invoices])
            }}</template>
          </pf-tooltip>

          <granted-link
            v-else-if="!product.rinnovo_automatico && product.days_to_expiration < 90"
            :force-disabled="!login.grants.billing"
            :to="{ name: 'product_renew', params: { id: product.id } }"
            :disabled-message="$t('Non hai i permessi per rinnovare il prodotto. Richiedili ad un utente amministratore')"
            type="primary"
            small
          >
            <template #icon>
              <rotate-right-icon />
            </template>
            {{ $t('Rinnova ora') }}
          </granted-link>

          <pf-dropdown v-else :container-ouia-id="`product-actions-${product.id}`" position="right">
            <template #toggle>
              <pf-menu-toggle small>{{ $t('Modifica') }}</pf-menu-toggle>
            </template>
            <pf-dropdown-list>
              <template v-if="product.durata != 'Una tantum'">
                <pf-tooltip v-if="product.grace_period && product.disdetto" position="left">
                  <granted-link
                    :to="{ name: 'product_renew', params: { id: product.id } }"
                    :disabled="!login.grants.billing"
                    disabled-message="Non hai i permessi per effettuare la modifica. Richiedili ad un utente amministratore."
                    type="dropdown-item"
                  >
                    <template #icon>
                      <rotate-right-icon />
                    </template>
                    {{ $t('Riattiva') }}
                  </granted-link>
                  <template #content>{{
                    $t('Clicca per procedere alla riattivazione di questo prodotto, i giorni rimasti per poter procedere sono {0}', [product.grace_period])
                  }}</template>
                </pf-tooltip>

                <template v-else-if="product.days_to_expiration >= 0">
                  <pf-dropdown-item
                    v-if="product.rinnovo_automatico"
                    :disabled="!login.grants.billing"
                    @click.prevent="setChangingProduct(idx, 'state', product.disdetto)"
                  >
                    <template #icon>
                      <rotate-right-icon v-if="product.disdetto" />
                      <pause-icon v-else />
                    </template>
                    {{ product.disdetto ? $t('Riattiva rinnovo automatico') : $t('Imposta in "non rinnovare" alla scadenza') }}
                  </pf-dropdown-item>

                  <pf-dropdown-item
                    v-if="!product.disdetto"
                    :disabled="!login.grants.billing"
                    @click.prevent="setChangingProduct(idx, 'autorenew', !product.rinnovo_automatico)"
                  >
                    <template #icon>
                      <rotate-right-icon v-if="!product.rinnovo_automatico" />
                      <hand-icon v-else />
                    </template>
                    {{ product.rinnovo_automatico ? $t('Imposta rinnovo manuale') : $t('Riattiva rinnovo automatico') }}
                  </pf-dropdown-item>
                </template>

                <pf-tooltip v-else position="left">
                  <granted-link :to="{ name: 'ticket', params: { id: 'new' } }" type="dropdown-item">
                    <template #icon>
                      <ticket-icon />
                    </template>
                    {{ $t('Apri ticket') }}
                  </granted-link>
                  <template #content>{{
                    $t(
                      'La data di rinnovo è passata, non è più possibile effettuare modifiche. Clicca qui per aprire un ticket al supporto e verificare la situazione.',
                    )
                  }}</template>
                </pf-tooltip>

                <granted-link
                  v-if="product.n_upgrades && (product.days_to_expiration >= 0 || (!product.disdetto && product.rinnovo_automatico))"
                  :force-disabled="!login.grants.billing"
                  :to="{ name: 'product_upgrade', params: { id: product.id } }"
                  type="dropdown-item"
                  :disabled-message="$t('Non hai i permessi per effettuare l\'upgrade del prodotto')"
                >
                  <template #icon>
                    <circle-arrow-up-icon />
                  </template>
                  {{ $t('Effettua un upgrade') }}
                </granted-link>
              </template>

              <pf-dropdown-item @click="productNotePopup = product">
                <template #icon>
                  <comment-icon />
                </template>
                {{ $t('Aggiungi una nota in fattura') }}
              </pf-dropdown-item>
            </pf-dropdown-list>
          </pf-dropdown>
        </pf-td>
      </pf-tr>
    </pf-tbody>
  </pf-table>

  <pf-empty-state v-else variant="xs">
    <pf-empty-state-header :title="$t('Nessun prodotto trovato con il filtro selezionato.')">
      <template #icon>
        <pf-empty-state-icon>
          <magnifying-glass-icon />
        </pf-empty-state-icon>
      </template>
    </pf-empty-state-header>
  </pf-empty-state>

  <prodotti-popup-note
    v-if="productNotePopup"
    v-model:customer-note="productNotePopup.note_cliente"
    :product-id="productNotePopup.id"
    @close="productNotePopup = null"
    @submit="submit"
  />

  <prodotti-popup-renewal-change
    v-if="changingProduct !== null && filteredOrderedProducts[changingProduct.product]"
    :idProduct="(filteredOrderedProducts[changingProduct.product] ?? ({} as NonNullable<(typeof filteredOrderedProducts)[number]>)).id"
    :variant="changingProduct.variant"
    v-model:renew="(filteredOrderedProducts[changingProduct.product] ?? ({} as NonNullable<(typeof filteredOrderedProducts)[number]>)).rinnovo_automatico"
    @submit="submit"
    @close="setChangingProduct(null)"
  />
</template>

<script lang="ts" setup>
import { reactive, ref, watch, type Reactive, type Ref, computed } from 'vue';
import { any, by } from '@common/utils';
import { type ProductOrder, type ProductsExpiringPayments } from '@/resources';
import ProdottiPopupNote from '../components/prodotti-popup-note.vue';
import ProdottiPopupRenewalChange from '../components/prodotti-popup-renewal-change.vue';
import TriangleExclamationIcon from '@vue-patternfly/icons/triangle-exclamation-icon';
import TicketIcon from '@vue-patternfly/icons/ticket-icon';
import CircleArrowUpIcon from '@vue-patternfly/icons/circle-arrow-up-icon';
import RotateRightIcon from '@vue-patternfly/icons/rotate-right-icon';
import SortLink from '../components/sort-link.vue';
import PauseIcon from '@vue-patternfly/icons/pause-icon';
import HandIcon from '@vue-patternfly/icons/hand-icon';
import GrantedLink from '../components/granted-link.vue';
import MagnifyingGlassIcon from '@vue-patternfly/icons/magnifying-glass-icon';
import CommentIcon from '@vue-patternfly/icons/comment-icon';
import { daysFromNow } from '@common/date';
import { useLoginStore } from '@/store/login';
import { PfEmptyState } from '@vue-patternfly/core';
import { isDefined } from '@vueuse/core';
import { useOUIAProps, type OUIAProps } from '@common/ouia';

const props = defineProps<
  OUIAProps & {
    products: ProductOrder[];
    payments?: Record<number, ProductsExpiringPayments>;
    hideDomainSort?: boolean;
    filter?: string;
    selectable?: boolean;
    selected?: number[];
  }
>();
const ouiaProps = useOUIAProps(props);

const emit = defineEmits<{
  (name: 'submit'): void;
  (name: 'toggle-select', ids: number[]): void;
}>();

const login = useLoginStore();

const changingProduct: Ref<{
  product: number;
  variant: 'state' | 'autorenew';
} | null> = ref(null);

const s: Ref<keyof ProductOrder> = ref('data_fine'); // sort by
const r = ref(false); // reverse sort

const filteredOrderedProducts: Reactive<(ProductOrder & { pending_invoices: string[] | null })[]> = reactive([]);
const filteredSelectedProductsIds = computed(() => {
  return props.selected ? props.selected.filter(id => filteredOrderedProducts.some(p => p.id === id)) : [];
});

const selectAllCheckboxState = computed(() => {
  return filteredOrderedProducts.length > 0 && filteredSelectedProductsIds.value.length === filteredOrderedProducts.length;
});

const selectAllIndeterminate = computed(() => {
  const selectedCount = filteredSelectedProductsIds.value.length;
  return selectedCount > 0 && selectedCount < filteredOrderedProducts.length;
});

watch(
  () => [props.filter, props.payments],
  () => {
    const products = props.filter ? props.products.filter(any(props.filter)) : props.products;
    changingProduct.value = null;
    filteredOrderedProducts.length = 0;

    filteredOrderedProducts.push(
      ...products
        .sort(
          by(p => {
            if (s.value === 'prezzo') {
              return Number(p.prezzo);
            }
            return p[s.value];
          }, r.value),
        )
        .map(p => {
          return {
            ...p,
            pending_invoices: props.payments?.[p.id] ?? null,
          };
        }),
    );
  },
  { immediate: true },
);

const productNotePopup: Ref<ProductOrder | null> = ref(null);

function handleSelectAll() {
  // Emetti tutti gli ID dei prodotti filtrati per il toggle
  const allIds = filteredOrderedProducts.map(p => p.id);
  emit('toggle-select', allIds);
}

function renewClass(disdetto: boolean) {
  return disdetto ? 'pf-v6-u-text-color-status-danger' : 'pf-v6-u-text-color-status-success';
}

function setChangingProduct(productIdx: number | null, variant?: 'state' | 'autorenew', renew?: boolean) {
  if (productIdx === null || !filteredOrderedProducts[productIdx]) {
    changingProduct.value = null;
    return;
  }

  if (isDefined(renew)) {
    filteredOrderedProducts[productIdx].rinnovo_automatico = renew;
  }

  changingProduct.value = {
    product: productIdx,
    variant: variant ?? 'state',
  };
}

function submit() {
  emit('submit');
}
</script>
